import { useAuthContext } from "../../auth/state/AuthContext";
import { DownloadAndSendIcon, DownloadIcon } from "../../shared/Icons";
import Header from "../../shared/app-shell/Header";
import PageFooter from "../../shared/app-shell/PageFooter";
import Sidebar from "../../shared/app-shell/Sidebar";
import {
  certificatesAtom,
  docAppendicesAtom,
  docTypesDropdownAtom,
  downloadProgressAtom,
  hasSearchedForCertificatesAtom,
  themeAtom,
  vesselDropdownInputAtom,
} from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { ToastContainer } from "../../shared/components/ToastContainer";
import WithTooltip from "../../shared/components/WithTooltip";
import { log, logE, logV } from "../../shared/services/debug-logger";
import { postData } from "../../shared/services/service";
import { downloadZip } from "../../shared/services/utils";
import { CertificatesHeader } from "../components/CertificatesHeader";
import { CertificatesTable } from "../components/CertificatesTable";
import SendDocumentsByEmailModal from "../components/SendDocumentsByEmailModal";
import { Certificate } from "../models/certificates.interfaces";
import { useCertificates, useDocAppendices, useDropdownVessels } from "../services/certificates.service";
import { datadogRum } from "@datadog/browser-rum";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import React, { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

const DOCUMENT_TYPES = ["Certificates", "Questionnaires", "Other documents"];

export type ValidationError = Partial<Record<"vesselName" | "documentTypes", string>>;

const Certificates: React.FC = () => {
  const theme = useAtomValue(themeAtom);

  const isLoading = false; // TODO Seems unused, (Had similar issue in tech forms service, we should see which pattern is better)
  const { vessels } = useDropdownVessels();
  const vessel = useAtomValue(vesselDropdownInputAtom);
  const [certsState, setCertificates] = useAtom(certificatesAtom);
  const [docAppendices] = useAtom(docAppendicesAtom);
  const { searchDocuments } = useCertificates();
  const { searchDocAppendices } = useDocAppendices();
  const setHasSearchedForCertificates = useSetAtom(hasSearchedForCertificatesAtom);
  const isSomethingSelected = certsState.some((cert) => cert.isChecked);
  const [selectedDocTypes] = useAtom(docTypesDropdownAtom);
  const { sub: email } = useAuthContext().state.user?.accessToken || {};
  const [selectedVessel] = useAtom(vesselDropdownInputAtom);
  logV("Certificates - BUILD", { count: certsState.length, selected: isSomethingSelected });

  const handleSearch = (documentTypes: string[]) => {
    log("Certificates - handleSearch", { hasVessel: !!vessel, types: documentTypes.length });
    if (vessel && documentTypes.length > 0) {
      datadogRum.addAction("searchDocument", {
        ext_user_email: email,
        documentTypes,
      });

      searchDocuments(vessel, documentTypes);
      searchDocAppendices(vessel);
    }
  };

  const setDownloadProgress = useSetAtom(downloadProgressAtom);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [sentCertificates, setSentCertificates] = useState<Certificate[]>([]);

  const sendFilesByEmail = async (): Promise<void> => {
    const selectedCerts = certsState.filter((certState) => certState.isChecked && docIsNotMissing(certState));
    datadogRum.addAction("sendDocumentByEmail", {
      ext_user_email: email,
      selectedCerts,
    });
    docAppendices.forEach((doc) => {
      if (selectedCerts.some((cert) => cert.certificate?.s3Path === doc?.appendixFor)) {
        selectedCerts.push({ certificate: doc, isChecked: true });
      }
    });

    setSentCertificates(selectedCerts.map(({ certificate }) => certificate));
    log("Certificates - sendFilesByEmail - open", { count: selectedCerts.length });
    setIsDownloadModalOpen(true);
  };

  // Emails are no longer sent via frontend, they are sent via backedn email sarvice v2.
  const downloadFiles = async (): Promise<void> => {
    log("Certificates - downloadFiles");
    setDownloadProgress(0);
    const selectedCerts = certsState.filter((certState) => certState.isChecked && docIsNotMissing(certState));
    datadogRum.addAction("downloadDocument", {
      ext_user_email: email,
      selectedCerts,
    });

    const urls: string[] = [];
    const fileNames: string[] = [];

    docAppendices.forEach((doc) => {
      if (selectedCerts.some((cert) => cert.certificate?.s3Path === doc?.appendixFor)) {
        selectedCerts.push({ certificate: doc, isChecked: true });
      }
    });
    const fetchUrlsAndFileNames = selectedCerts.map(async (certState): Promise<void> => {
      const certificate = certState.certificate;
      const fileName = getFileName(certificate);
      try {
        const {
          data: { url },
        } = await postData(`documents/presigned-url`, {
          documentKey: certificate.s3Path,
        });

        urls.push(url);
        fileNames.push(fileName);
      } catch (err) {
        // The aggregate below reports failed URL preparation without one log per file.
      }
    });

    await Promise.all(fetchUrlsAndFileNames);
    log("Certificates - downloadFiles - prepared", { count: urls.length, failed: selectedCerts.length - urls.length });

    try {
      downloadZip(
        {
          fileNames,
          urls,
        },

        setDownloadProgress,
      );
    } catch (err) {
      logE(new Error("Certificates - downloadFiles - failed"));
    }
  };
  const getFileName = (certificate: Certificate) => {
    let certName = certificate?.docName;
    const imoNumber = certificate.imoNumber;
    const vesselName = certificate.vesselName;
    if (certificate?.docType === "Appendix") {
      certName = certsState
        .filter((doc) => doc.certificate.s3Path === certificate.appendixFor)
        .map((e) => e.certificate.docName)?.[0];
    }
    const fileName = `${certName}_${vesselName}_${imoNumber}`.replace(/\s+/g, "").replace(/\//g, "-").trim();

    if (certificate?.docType === "Appendix") {
      const certAppendices = docAppendices.filter((doc) => doc?.appendixFor === certificate.appendixFor);
      const certIndex = certAppendices?.findIndex((cert) => cert.s3Path === certificate.s3Path);
      const docfileName = `${fileName}_Appendix_${certIndex}`;
      return docfileName;
    } else return fileName;
  };
  const handleCloseModal = (email: string) => {
    log("Certificates - handleCloseModal");
    setIsDownloadModalOpen(false);
    setSentCertificates([]);
  };

  const clearTable = () => {
    log("STATE - clearTable");
    setCertificates([]);
    setHasSearchedForCertificates(false);
  };

  useEffect(() => {
    return () => {
      clearTable();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorClassName="text-[#00a194]" loadingIndicatorSize="lg">
      <div className="flex h-screen">
        <Sidebar />

        <div
          className={twMerge(
            "flex max-h-screen flex-1 flex-col overflow-y-hidden bg-greyscale-10",
            theme.home.tailwind.pageBackground,
          )}
        >
          <Header />

          <h1
            className={twMerge(
              "pb-3 pl-6 pt-8 text-2xl font-semibold md:mb-0 md:mr-4",
              theme.myVesselsTable.tailwind.textDefault,
            )}
          >
            Certificates and documents
          </h1>
          <h3
            className={twMerge(
              "pb-6 pl-6 text-sm font-normal md:mb-0 md:mr-4",
              theme.myVesselsTable.tailwind.textDefault,
            )}
          >
            Manage all certificates and documents from a specific vessel
          </h3>

          <main className="flex flex-1 flex-col overflow-y-hidden">
            <ToastContainer />
            <CertificatesHeader vesselOptions={vessels} documentTypeOptions={DOCUMENT_TYPES} onSearch={handleSearch} />
            {selectedVessel && (
              <div
                className={twMerge(
                  "flex items-center gap-2 border-b px-6 py-4 text-sm",
                  theme.uwhcAssistant.background,
                  theme.uwhcAssistant.border,
                  theme.myVesselsTableRow.row.text,
                )}
              >
                {isSomethingSelected && (
                  <>
                    <div
                      className={twMerge("flex cursor-pointer items-center gap-2 px-4 py-2 text-[#00a194]")}
                      onClick={downloadFiles}
                    >
                      <DownloadIcon />
                      <WithTooltip
                        id="download-zip"
                        content="Download files archived. Takes a few seconds."
                        place="top"
                        tooltipClasses="max-w-[180px] z-[100]"
                      >
                        Download
                      </WithTooltip>
                    </div>
                    <div
                      className="flex cursor-pointer items-center gap-2 px-4 py-2 text-[#00a194]"
                      onClick={sendFilesByEmail}
                    >
                      <DownloadAndSendIcon />
                      <WithTooltip
                        id="send-email"
                        content="Send files by email. Takes a few seconds."
                        place="top"
                        tooltipClasses="max-w-[180px] z-[100]"
                      >
                        Share by email
                      </WithTooltip>
                    </div>
                    {/* TODO: Unncomment this when the feature is ready */}
                    {/* <div className="flex cursor-pointer items-center gap-2 px-4 py-2 text-[#00a194]">Request valid</div> */}
                    <div className="h-9 w-px bg-[#DFE4E2]" aria-hidden="true" />
                  </>
                )}
                <a
                  className="px-4 py-2"
                  href={`/documents/request?vesselName=${vessel?.vesselName}&vesselCode=${vessel?.code}&imoNumber=${vessel?.imoNumber}`}
                >
                  Request document
                </a>
              </div>
            )}

            <CertificatesTable
              openDownloadModal={(certificates) => {
                setSentCertificates(certificates);
                setIsDownloadModalOpen(true);
              }}
              searchDocs={() => setTimeout(() => handleSearch(selectedDocTypes), 1000)}
            />
            {/* <DocumentDownloadModal isOpen={isDownloadModalOpen} onModalClose={handleCloseModal} /> DEPRECATED */}
            <SendDocumentsByEmailModal
              isOpen={isDownloadModalOpen}
              certificates={sentCertificates}
              onModalClose={handleCloseModal}
            />
          </main>

          <PageFooter />
        </div>
      </div>
    </LoadingWrapper>
  );
};

function docIsNotMissing(certState: { certificate: Certificate; isChecked: boolean }): boolean {
  return !certState.certificate.s3Path?.includes("ERROR_LOADING");
}

export default Certificates;
